import api from './api';

/**
 * OMG Experience Service
 * Handles all API calls related to OMG Experience bookings
 * (20% markup on shared rooms - Daya/Meera - for staycations)
 */

const omgExperienceService = {
  /**
   * Calculate price for OMG Experience booking (with 20% markup)
   * @param {Object} bookingData - Booking data containing rooms, beds, addOns
   * @returns {Promise} Response with pricing breakdown
   */
  calculatePrice: async (bookingData) => {
    try {
      const response = await api.post('/api/omg-experience/calculate-price', bookingData);
      console.log('OMG Calculate Price Raw Response:', response);
      
      // The API interceptor already unwraps response.data, and the OMG API returns { success, data: { ... } }
      // So response will be { success: true, data: { totalPrice, breakdown, ... } }
      // We need to return the nested 'data' object to match the regular bookings API format
      if (response.success && response.data) {
        return { data: response.data };
      }
      
      // Fallback: if the response is already in the correct format
      return response.data ? response : { data: response };
    } catch (error) {
      console.error('Error calculating OMG Experience price:', error);
      throw error;
    }
  },

  /**
   * Create OMG Experience booking for staff (no payment required)
   * @param {Object} bookingData - Complete booking data
   * @returns {Promise} Created booking response
   */
  createStaffBooking: async (bookingData) => {
    try {
      const response = await api.post('/api/omg-experience/staff-booking', bookingData);
      return response.data;
    } catch (error) {
      console.error('Error creating OMG Experience staff booking:', error);
      throw error;
    }
  },

  /**
   * Create OMG Experience booking via staff-reservation endpoint
   * (Alias endpoint that integrates with staff workflow)
   * @param {Object} bookingData - Complete booking data
   * @returns {Promise} Created booking response
   */
  createStaffReservation: async (bookingData) => {
    try {
      const response = await api.post('/api/omg-experience/staff-reservation', bookingData);
      return response.data;
    } catch (error) {
      console.error('Error creating OMG Experience staff reservation:', error);
      throw error;
    }
  },

  /**
   * Get pricing information for a specific room type
   * @param {string} roomTypeId - Room type UUID
   * @returns {Promise} Pricing information with markup details
   */
  getPricingInfo: async (roomTypeId) => {
    try {
      const response = await api.get(`/api/omg-experience/pricing/${roomTypeId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting OMG Experience pricing info:', error);
      throw error;
    }
  },

  /**
   * Validate if booking is eligible for OMG Experience
   * @param {Object} bookingData - Booking data to validate
   * @returns {Promise} Validation response
   */
  validateEligibility: async (bookingData) => {
    try {
      const response = await api.post('/api/omg-experience/validate', bookingData);
      return response.data;
    } catch (error) {
      console.error('Error validating OMG Experience eligibility:', error);
      throw error;
    }
  },

  /**
   * Check if a room type is a shared room (Daya/Meera)
   * @param {string} roomTypeName - Name of the room type
   * @returns {boolean} True if shared room eligible for OMG
   */
  isSharedRoomType: (roomTypeName) => {
    if (!roomTypeName) return false;
    
    // Normalize the room type name for flexible matching
    const normalized = roomTypeName.toLowerCase().trim();
    
    // Check if it contains "daya" or "meera" with "sharing" or "shared"
    const isDaya = normalized.includes('daya') && (normalized.includes('sharing') || normalized.includes('shared'));
    const isMeera = normalized.includes('meera') && (normalized.includes('sharing') || normalized.includes('shared'));
    
    console.log('OMG Room Type Check:', {
      roomTypeName,
      normalized,
      isDaya,
      isMeera,
      isEligible: isDaya || isMeera
    });
    
    return isDaya || isMeera;
  },

  /**
   * Get OMG Experience markup percentage
   * @returns {number} Markup percentage (0.20 for 20%)
   */
  getMarkupPercentage: () => {
    return 0.20; // 20% markup
  },

  /**
   * Calculate markup amount for a given base price
   * @param {number} basePrice - Base price before markup
   * @returns {number} Markup amount
   */
  calculateMarkupAmount: (basePrice) => {
    return basePrice * 0.20;
  }
};

export default omgExperienceService;
